import { create } from 'zustand';

import { apiDomain, basename } from '../setting';
import useModalStore from './useModalStore';
import useAuthStore from './useAuthStore';
import useLoadingStore from './useLoadingStore';
import useAuthAdminStore from './useAuthAdminStore';
import withErrorHandling from '../function/withErrorHandling';

const useAdminAccountStore = create((set, get) => ({
    page: 1,
    setPage: (value) => set((state) => {
        return { page: value }
    }),

    recordperpage: 10,
    setRecordPerPage: (value) => set((state) => {
        return { recordperpage: value }
    }),

    ttlpage: null,
    setTtlPage: (value) => set((state) => {
        return { ttlpage: value }
    }),

    selectedTab: 'CLM',
    setSelectedTab: (value) => set((state) => {
        return { selectedTab: value }
    }),

    searchText: "",
    setSearchText: (value) => set((state) => {
        return { searchText: value }
    }),

    sort: "createdt desc",
    setSort: (value) => set((state) => {
        return { sort: value }
    }),

    role_filter: undefined,
    setRoleFilter: (value) => set((state) => {
        return { role_filter: value }
    }),

    accountList: [],
    roleList: [],
    CSCount: "",
    ClmCount: "",
    AllCount: "",
    admListStaff: async () => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { searchText, selectedTab, sort, role_filter, page } = get();

        const response = await fetch(`${apiDomain}/${basename}/api/preauthAdmin/admListStaff`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "search": searchText,
                "team": selectedTab,
                "role": role_filter,
                "sort": sort,
                "recordperpage": "10",
                "page": page,
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        set({
            accountList: data.list.records,
            ttlpage: data.list.ttlpage,
            CSCount: data.noOfCSTeam,
            ClmCount: data.noOfCLMTeam,
            AllCount: data.noOfAllUser,
            roleList: data.roleList,
        });
    },

    role: undefined,
    setRole: (value) => set((state) => {
        return { role: value }
    }),
    isValid_role: true,
    setIsValid_role: (value) => set((state) => {
        return { isValid_role: value }
    }),

    firstName: "",
    setFirstName: (value) => set((state) => {
        return { firstName: value }
    }),
    isValid_firstName: true,
    setIsValid_firstName: (value) => set((state) => {
        return { isValid_firstName: value }
    }),

    lastName: "",
    setLastName: (value) => set((state) => {
        return { lastName: value }
    }),
    isValid_lastName: true,
    setIsValid_lastName: (value) => set((state) => {
        return { isValid_lastName: value }
    }),

    email: "",
    setEmail: (value) => set((state) => {
        return { email: value }
    }),
    isValid_email: true,
    setIsValid_email: (value) => set((state) => {
        return { isValid_email: value }
    }),

    lanId: "",
    setLanId: (value) => set((state) => {
        return { lanId: value }
    }),
    isValid_lanId: true,
    setIsValid_lanId: (value) => set((state) => {
        return { isValid_lanId: value }
    }),

    admOpenStaff: async (staffId) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preauthAdmin/admOpenStaff?staffId=${staffId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        set({
            role: data.staff.role,
            firstName: data.staff.staffFirstName,
            lastName: data.staff.staffLastName,
            email: data.staff.staffEmail,
            lanId: data.staff.LanID ?? "",
        });
    },

    resetAccountModal: () => {
        set({
            role: undefined,
            isValid_role: true,
            firstName: "",
            isValid_firstName: true,
            lastName: "",
            isValid_lastName: true,
            email: "",
            isValid_email: true,
            lanId: "",
            isValid_lanId: true,
        });
    },

    admUpdateStaffStatus: async (staffId, status) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { admListStaff } = get();

        const response = await fetch(`${apiDomain}/${basename}/api/preauthAdmin/admUpdateStaffStatus`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "staffId": staffId,
                "status": status,
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        withErrorHandling(admListStaff());
    },

    admUpdateStaff: async (staffId = "") => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { firstName, lastName, email, role, lanId } = get();
        const { admListStaff } = get();

        const response = await fetch(`${apiDomain}/${basename}/api/preauthAdmin/admUpdateStaff`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "staffId": staffId,
                "staffFirstName": firstName,
                "staffLastName": lastName,
                "staffEmail": email,
                "role": role,
                "LanID": lanId,
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        withErrorHandling(admListStaff());
    },

    isSortingOpen: false,
    setIsSortingOpen: (value) => set((state) => {
        return { isSortingOpen: value }
    }),


}))

export default useAdminAccountStore;